package com.taocent.simple.compose.component.testscreens

import androidx.compose.foundation.Image
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.ArrowLeft
import com.taocent.simple.compose.component.blockrichtext.BlockRichTextEditor
import com.taocent.simple.compose.component.blockrichtext.BlockState
import com.taocent.simple.compose.component.blockrichtext.ExperimentalBlockRichTextApi
import com.taocent.simple.compose.component.blockrichtext.rememberBlockState
import com.taocent.simple.compose.component.getPlatform
import com.taocent.simple.compose.component.richtext.core.internal.platform.rememberKeyboardHeight
import com.taocent.simple.compose.component.richtext.core.internal.platform.rememberKeyboardNeedsInsets
import kotlin.math.roundToInt

@OptIn(ExperimentalBlockRichTextApi::class)
@Composable
fun TestBlockRichTextEditor(onBack: () -> Unit, showTopBar: Boolean = true) {
    val state = rememberBlockState()
    var showDebug by remember { mutableStateOf(false) }

    if (showTopBar) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { Text("块级富文本编辑器") },
                    navigationIcon = {
                        TextButton(onClick = onBack) {
                            Image(
                                Lucide.ArrowLeft,
                                contentDescription = "返回",
                                modifier = Modifier.size(18.dp)
                            )
                        }
                    },
                    actions = {
                        TextButton(onClick = { showDebug = !showDebug }) {
                            Text(
                                text = if (showDebug) "关闭" else "Debug",
                                color = if (showDebug) MaterialTheme.colorScheme.primary
                                else MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.surfaceContainer,
                        titleContentColor = MaterialTheme.colorScheme.onSurface
                    )
                )
            }
        ) { padding ->
            Column(modifier = Modifier.padding(padding)) {
                if (showDebug) {
                    DebugStatusPanel(state)
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
                BlockRichTextEditorContent(state, Modifier.fillMaxSize())
            }
        }
    } else {
        BlockRichTextEditorContent(state, Modifier.fillMaxSize())
    }
}

@OptIn(ExperimentalBlockRichTextApi::class)
@Composable
private fun DebugStatusPanel(state: BlockState) {
    val keyboardHeightDp = rememberKeyboardHeight()
    val needsInsets = rememberKeyboardNeedsInsets()

    Surface(
        color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.9f),
        modifier = Modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .horizontalScroll(rememberScrollState())
                .padding(horizontal = 8.dp, vertical = 6.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            DebugChip("键盘高度", "${keyboardHeightDp.value.roundToInt()}dp")
            DebugChip(
                "键盘可见",
                if ((keyboardHeightDp.value > 0f)) "是" else "否",
                highlight = keyboardHeightDp.value > 0f,
            )
            DebugChip("NSInsets", if (needsInsets) "是" else "否")
            DebugChip("焦点块", state.focusedBlockId.takeLast(6))
            DebugChip("块数", "${state.blocks.size}")
            DebugChip("曾聚焦", if (state.hasUserFocusedAnyField) "是" else "否")
            DebugChip("可撤销", if (state.canUndo) "是" else "否")
            DebugChip("可重做", if (state.canRedo) "是" else "否")
            val guard = state.backspaceBlockDeleteGuard
            DebugChip(
                "删除锁",
                if (guard) "已锁" else "正常",
                highlight = guard,
            )
        }
    }
}

@Composable
private fun DebugChip(label: String, value: String, highlight: Boolean = false) {
    Surface(
        shape = RoundedCornerShape(6.dp),
        color = if (highlight) MaterialTheme.colorScheme.primaryContainer
        else MaterialTheme.colorScheme.surface
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 3.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = label,
                fontSize = 10.sp,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                fontWeight = FontWeight.Medium,
            )
            Text(
                text = value,
                fontSize = 11.sp,
                fontFamily = FontFamily.Monospace,
                color = if (highlight) MaterialTheme.colorScheme.onPrimaryContainer
                else MaterialTheme.colorScheme.onSurface,
                fontWeight = FontWeight.Bold,
            )
        }
    }
}

@OptIn(ExperimentalBlockRichTextApi::class)
@Composable
private fun BlockRichTextEditorContent(
    state: BlockState,
    modifier: Modifier
) {
    Column(modifier = modifier) {
        BlockRichTextEditor(
            state = state,
            modifier = Modifier.fillMaxSize()
        )

        HorizontalDivider()

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .padding(16.dp)
        ) {
            Text(
                text = "使用说明",
                style = MaterialTheme.typography.titleSmall,
                color = MaterialTheme.colorScheme.primary
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = """
                    • 块级富文本编辑器支持多文本块
                    • 每个文本块独立编辑，支持完整的富文本格式化
                    • 选中文本后浮动工具栏支持格式化、复制、剪切、粘贴、全选
                    • 底部工具栏支持撤销、重做、表情、文字样式、超链接、导出
                    • 撤销/重做跨块全局管理
                """.trimIndent(),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}
